import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import { Avatar, Box, Typography } from "@mui/material";
import { City, Professional, Team } from "@syadem/ariane-js";
import { isEqual } from "lodash-es";
import Polyglot from "node-polyglot";
import { JSX, useEffect, useState } from "react";
import mesVaccinsLogo from "../../../assets/mesvaccinsLogoSquare.jpeg";
import { useArianeApi } from "../../hooks/useArianeApi";
import { useI18n } from "../../hooks/useI18n";
import { theme } from "../../layout/Theme";
import { StyledAutocomplete } from "../mui/StyledAutoComplete";

type ProfessionalSearchResult<ProfessionalsOnly extends boolean> =
  ProfessionalsOnly extends true ? Professional : Professional | Team;

export function ProfessionalSearchField<ProfessionalsOnly extends boolean = false>({
  city,
  professional,
  professionalError,
  professionalsOnly,
  onProfessionalChange,
  onArianeError,
}: {
  city?: City;
  professional?: ProfessionalSearchResult<ProfessionalsOnly>;
  professionalError?: boolean;
  professionalsOnly?: ProfessionalsOnly;
  onProfessionalChange: (professional?: ProfessionalSearchResult<ProfessionalsOnly>) => void;
  onArianeError?: (error: unknown) => void;
}) {
  const { t } = useI18n();
  const arianeApi = useArianeApi();
  const [professionalOptions, setProfessionalOptions] = useState<(Professional | Team)[]>([]);
  const [professionalInput, setProfessionalInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async function () {
      if (city) {
        try {
          setIsLoading(true);
          const results = await arianeApi.professionals.searchByNameAndZipCodes(
            professionalInput,
            city.zip_code,
          );
          setProfessionalOptions(
            professionalsOnly ? results.filter((result) => result.type === "professional") : results,
          );
          onArianeError?.(undefined);
        } catch (error) {
          // TODO: report error to error reporting service
          onArianeError?.(error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setProfessionalOptions([]);
        if (professional !== undefined) {
          onProfessionalChange(undefined);
        }
      }
    })();
  }, [
    arianeApi.professionals,
    city,
    professional,
    professionalsOnly,
    onArianeError,
    onProfessionalChange,
    professionalInput,
  ]);

  return (
    <StyledAutocomplete
      open={open}
      onOpen={() => {
        if (professionalInput) {
          setOpen(true);
        }
      }}
      onClose={() => setOpen(false)}
      disabled={!city}
      options={professionalOptions}
      value={professional ?? null}
      onInputChange={(_event, newProfessionalInput) => {
        setProfessionalInput(newProfessionalInput);
        if (!newProfessionalInput) {
          setOpen(false);
        }
      }}
      onChange={(_event, newProfessional) => {
        if (professionalsOnly && newProfessional?.type === "team") {
          return;
        }
        onProfessionalChange(
          (newProfessional ?? undefined) as ProfessionalSearchResult<ProfessionalsOnly> | undefined,
        );
        if (!newProfessional) {
          setOpen(false);
        }
      }}
      getOptionLabel={renderLabel}
      testId="professional"
      required
      label={t("pro")}
      placeholder={`ex : Jean Marceau`}
      error={professionalError || undefined}
      loading={isLoading}
      fullWidth
      renderOption={(props, option) => (
        <li data-testid="professionalOption" {...props}>
          {renderResult(option, t)}
        </li>
      )}
      isOptionEqualToValue={isEqual}
    />
  );
}

function renderLabel(result: Professional | Team) {
  if (isProfessional(result)) {
    return `${result.first_name} ${result.last_name}`;
  }
  if (isTeam(result)) {
    return result.name ?? "";
  }
  return "";
}

function renderResult(
  result: Professional | Team,
  t: (phrase: string, options?: number | Polyglot.InterpolationOptions | undefined) => string,
) {
  if (isProfessional(result)) {
    return renderProfessional(result, t);
  }
  if (isTeam(result)) {
    return renderTeam(result, t);
  }
}

function isProfessional(result: Professional | Team): result is Professional {
  return result.type === "professional";
}

function isTeam(result: Professional | Team): result is Team {
  return result.type === "team";
}

function renderProfessional(
  professional: Professional,
  t: (phrase: string, options?: number | Polyglot.InterpolationOptions | undefined) => string,
): JSX.Element {
  return (
    <Box display="flex" justifyContent="space-between" width="100%">
      <Typography flex="1 1 auto" fontSize="0.8rem">
        {`${professional.first_name} ${professional.last_name}`}
        <br />
        {professional.address}
        <br />
        {`${professional.zip_code} ${professional.city?.toUpperCase()}`}
      </Typography>
      <Box display="flex" alignItems="center" justifyContent="center">
        {professional.id && (
          <Avatar
            src={mesVaccinsLogo}
            title={t("common.subscribed")}
            sx={{ width: 20, height: 20, mr: 1 }}
            alt="mesvaccins.net logo"
          />
        )}
        <PersonIcon sx={{ color: theme.palette.primary[500] }} />
      </Box>
    </Box>
  );
}

function renderTeam(
  team: Team,
  t: (phrase: string, options?: number | Polyglot.InterpolationOptions | undefined) => string,
): JSX.Element {
  return (
    <Box display="flex" justifyContent="space-between" width="100%">
      <Typography flex="1 1 auto" fontSize="0.8rem">
        {team.name}
        <br />
        {team.address}
      </Typography>
      <Box display="flex" alignItems="center" justifyContent="center">
        {team.id && (
          <Avatar
            src={mesVaccinsLogo}
            title={t("common.subscribed")}
            sx={{ width: 20, height: 20, mr: 1 }}
            alt="mesvaccins.net logo"
          />
        )}
        <GroupsIcon />
      </Box>
    </Box>
  );
}
